import { StationEvent, StationEventType } from '../../../services/models/stationEvent';

export const EVENT_TYPE_LABELS: Record<StationEventType, string> = {
  lock_open: 'Ouverture lock',
  lock_close: 'Fermeture lock',
  lock_status_open: 'Lock ouvert',
  lock_status_close: 'Lock fermé',
  lock_activate: 'Activation lock',
  lock_deactivate: 'Désactivation lock',
  code_used: 'Code utilisé',
  alarm_activate: 'Activation alarme',
  alarm_deactivate: 'Désactivation alarme',
  bus_activate: 'Activation bus',
  bus_deactivate: 'Désactivation bus',
  main_lock_activate: 'Activation verrou principal',
  main_lock_deactivate: 'Désactivation verrou principal',
  reboot: 'Redémarrage station',
  recovery: 'Récupération réseau',
  station_activate: 'Station activée',
  station_deactivate: 'Station désactivée',
};

export const EVENT_TYPE_COLORS: Record<StationEventType, string> = {
  lock_open: 'bg-emerald-500',
  lock_close: 'bg-emerald-500',
  lock_status_open: 'bg-cyan-500',
  lock_status_close: 'bg-cyan-700',
  lock_activate: 'bg-sky-500',
  lock_deactivate: 'bg-neutral-40',
  code_used: 'bg-amber-500',
  alarm_activate: 'bg-rose-500',
  alarm_deactivate: 'bg-neutral-40',
  bus_activate: 'bg-violet-500',
  bus_deactivate: 'bg-neutral-40',
  main_lock_activate: 'bg-indigo-500',
  main_lock_deactivate: 'bg-neutral-40',
  reboot: 'bg-orange-500',
  recovery: 'bg-yellow-500',
  station_activate: 'bg-success-500',
  station_deactivate: 'bg-neutral-40',
};

export const STATUS_BADGE: Record<string, { bg: string; text: string; label: string; tooltip: string }> = {
  ok: { bg: 'bg-success-100', text: 'text-success-600', label: 'OK', tooltip: 'Action confirmée par la station' },
  info: { bg: 'bg-neutral-5', text: 'text-neutral-60', label: 'Info', tooltip: 'Événement informatif' },
  error: { bg: 'bg-error-100', text: 'text-error-600', label: 'Erreur', tooltip: 'Action échouée ou expirée' },
  pending: { bg: 'bg-neutral-10', text: 'text-neutral', label: 'En attente', tooltip: 'En attente de confirmation station' },
};

export const formatDateTime = (date: string) =>
  new Date(date).toLocaleString('fr-FR', {
    day: '2-digit', month: '2-digit', hour: '2-digit', minute: '2-digit', second: '2-digit',
  });

export const getTooltipLines = (event: StationEvent, baseTooltip: string): string[] => {
  const lines = [baseTooltip];
  if (event.status === 'error' && event.updatedAt) {
    lines.push(`Déclenché le ${formatDateTime(event.createdAt)}`);
    lines.push(`Timeout le ${formatDateTime(event.updatedAt)}`);
  } else if (event.status === 'pending') {
    lines.push(`Déclenché le ${formatDateTime(event.createdAt)}`);
  }
  return lines;
};

const formatUptime = (seconds?: number): string | null => {
  if (typeof seconds !== 'number' || seconds < 0) return null;
  const d = Math.floor(seconds / 86400);
  const h = Math.floor((seconds % 86400) / 3600);
  const m = Math.floor((seconds % 3600) / 60);
  if (d > 0) return `${d}j ${h}h`;
  if (h > 0) return `${h}h ${m}min`;
  return `${m}min`;
};

// Recoveries are autonomous and always informational, so they render a plain
// "Info" badge. The generic action-lifecycle tooltip ("Événement informatif")
// is uninformative here, so build a recovery-specific one that explains the
// reconnection and shows the station's uptime.
export const getRecoveryBadge = (
  event: StationEvent,
): { bg: string; text: string; label: string; tooltipLines: string[] } => {
  const meta = (event.metadata ?? {}) as { uptimeSeconds?: number };

  const lines = [
    'Reconnexion réseau automatique de la station',
    `Détectée le ${formatDateTime(event.createdAt)}`,
  ];
  const uptime = formatUptime(meta.uptimeSeconds);
  if (uptime) lines.push(`Uptime : ${uptime}`);

  return { bg: 'bg-neutral-5', text: 'text-neutral-60', label: 'Info', tooltipLines: lines };
};
